<template>
  <div class="schedule-entry-wrapper">
    <div class="schedule-entry-header">
      <div class="header-text">
        <h5 class="mb-2 text-h6 text-typo font-weight-bold">
          排班明細:<span style="color: red">{{ rocYearMonth }}</span>
          排班類型:{{ formatScheduleType(schedule_type) }}
          <span
            v-if="(errors || []).length > 0"
            class="ml-2"
            style="color: red; font-size: 0.85em"
          >
            錯誤:{{ (errors || []).length }}
          </span>
          <span
            v-if="(warnings || []).length > 0"
            class="ml-2"
            style="color: orange; font-size: 0.85em"
          >
            警告:{{ (warnings || []).length }}
          </span>
        </h5>
      </div>
      <div class="d-flex">
        <v-btn
          v-if="canImport"
          color="primary"
          elevation="0"
          :ripple="false"
          height="36"
          class="
            font-weight-bold
            text-capitalize
            btn-primary
            bg-gradient-primary
            px-4
          "
          @click="$emit('import')"
        >
          <v-icon left>mdi-import</v-icon>
          匯入
        </v-btn>
        <v-btn
          v-if="canImport"
          color="error"
          elevation="0"
          :ripple="false"
          height="36"
          class="
            font-weight-bold
            text-capitalize
            btn-danger
            bg-gradient-danger
            px-4
            ml-2
          "
          @click="$emit('clear')"
        >
          <v-icon left>mdi-close-circle</v-icon>
          清除
        </v-btn>
      </div>
    </div>

    <div class="schedule-entry-table">
      <table>
        <thead>
          <tr>
            <th class="sticky-col">工號</th>
            <th class="sticky-col-2">姓名</th>
            <th class="sticky-col-3">類別</th>
            <th v-for="d in days" :key="`h-${d}`" class="day-col">
              {{ d }}
            </th>
            <th class="summary-col">國定假日</th>
            <th class="remark-col">備註</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(block, blockIdx) in entryBlocks">
            <tr
              v-for="(row, rowIdx) in block.rows"
              :key="`row-${blockIdx}-${rowIdx}`"
            >
              <td
                v-if="rowIdx === 0"
                class="sticky-col"
                :class="{ 'existing-entry-cell': block.hasExistingSchedule }"
                :rowspan="block.rows.length"
              >
                <div>{{ block.emp_id }}</div>
                <div
                  v-if="block.hasExistingSchedule"
                  class="existing-entry-label"
                >
                  覆蓋原有資料
                </div>
              </td>
              <td
                v-if="rowIdx === 0"
                class="sticky-col-2"
                :class="{ 'existing-entry-cell': block.hasExistingSchedule }"
                :rowspan="block.rows.length"
              >
                <div class="d-flex align-center">
                  <span>{{ block.name }}</span>
                  <v-btn
                    v-if="block.entry_id"
                    x-small
                    icon
                    class="ml-1"
                    title="檢視歷史紀錄"
                    @click="$emit('view-history', block)"
                  >
                    <v-icon small>mdi-history</v-icon>
                  </v-btn>
                </div>
                <div
                  v-if="block.hasExistingSchedule"
                  class="existing-entry-label"
                >
                  覆蓋原有資料
                </div>
              </td>
              <td class="sticky-col-3">{{ row.kindLabel }}</td>
              <td
                v-for="d in days"
                :key="`c-${blockIdx}-${rowIdx}-${d}`"
                class="day-col"
                :style="getCellStyle(row.kind, row.values[d], block, d)"
                :title="getCellTitle(row.kind, row.values[d], block, d)"
              >
                {{ row.values[d] }}
              </td>
              <td
                v-if="rowIdx === 0"
                class="summary-col"
                :rowspan="block.rows.length"
              >
                <div
                  v-if="block.holidaySummary"
                  class="text-caption text-left"
                  style="color: #c65911; font-weight: 600"
                >
                  未排: {{ block.holidaySummary.missing }}<br />
                  可用: {{ block.holidaySummary.available }}
                </div>
              </td>
              <td
                v-if="rowIdx === 0"
                class="remark-col"
                :rowspan="block.rows.length"
              >
                <div
                  v-for="(msg, idx) in getBlockRemarkMessages(block)"
                  :key="`remark-${blockIdx}-${idx}`"
                  class="remark-line"
                  :class="{
                    'error-remark': msg.isError,
                    'warning-remark': msg.isWarning,
                  }"
                >
                  {{ msg.text }}
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { formatScheduleType } from "@/utils/formatters";

export default {
  name: "ScheduleEntryWidget",
  emits: ["show-table", "import", "clear", "view-history"],
  props: {
    entries: {
      type: Array,
      default: () => [],
    },
    errors: {
      type: Array,
      default: () => [],
    },
    warnings: {
      type: Array,
      default: () => [],
    },
    nationalHolidaySummaries: {
      type: Array,
      default: () => [],
    },
    year: {
      type: [String, Number],
      required: true,
    },
    month: {
      type: [String, Number],
      required: true,
    },
    shiftTypes: {
      type: Array,
      default: () => [],
    },
    schedule_type: {
      type: String,
      default: "",
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    canImport: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    days() {
      return Array.from({ length: 31 }, (_, i) => i + 1);
    },
    rocYearMonth() {
      const rocYear = this.year - 1911;
      const paddedMonth = String(this.month).padStart(2, "0");
      return `${rocYear}年${paddedMonth}月`;
    },
    entryBlocks() {
      const userMap = {};
      (this.entries || []).forEach((entry) => {
        const u = entry.user || entry.employee || {};
        const uid = u.id || u.user_id;
        if (uid) {
          userMap[uid] = u.name || "";
        }
      });

      return (this.entries || []).map((entry) => {
        const u = entry.user || entry.employee || {};
        const base = {
          entry_id: entry.id || null,
          user_id: u.id || u.user_id || "",
          emp_id: u.member_id || u.emp_id || "",
          name: u.name || "",
          hasExistingSchedule: Boolean(entry.hasExistingSchedule),
        };

        const shiftChanges = {};
        for (let d = 1; d <= 31; d++) {
          const sc = entry[`day${d}_shift_change`];
          const scDay = entry[`day${d}_shift_change_day`];
          if (sc) {
            const myId = base.user_id;
            const targetId =
              String(sc.from_user_id) === String(myId)
                ? sc.to_user_id
                : sc.from_user_id;
            shiftChanges[d] = {
              otherName: userMap[targetId] || `用戶ID:${targetId}`,
              otherDay: scDay,
            };
          }
        }

        if (entry.schedule_type) {
          this.$emit("update:schedule_type", entry.schedule_type);
        }

        const userSummaries = (this.nationalHolidaySummaries || []).filter(
          (summary) => {
            if (summary == null) return false;
            const userId = summary.user_id ?? summary.user?.id ?? null;
            const memberId = summary.user?.member_id ?? null;
            return (
              (userId != null && String(base.user_id) === String(userId)) ||
              (memberId != null && String(base.emp_id) === String(memberId))
            );
          }
        );

        const cmpYear = Number(this.year);
        const cmpMonth = Number(this.month);

        const monthSummaries = userSummaries.filter((s) => {
          return Number(s.year) === cmpYear && Number(s.month) === cmpMonth;
        });

        const missing = monthSummaries.reduce(
          (acc, s) => acc + (Number(s.missing_holiday_count) || 0),
          0
        );
        const used = monthSummaries.reduce(
          (acc, s) => acc + (Number(s.used_holiday_count) || 0),
          0
        );

        let holidaySummary = null;
        if (monthSummaries.length > 0 && missing > 0) {
          const available = Math.max(0, missing - used);
          holidaySummary = { missing, used, available };
        }

        return {
          ...base,
          shiftChanges,
          holidaySummary,
          rows: [
            {
              kind: "status",
              kindLabel: "狀態",
              values: this.buildDayValues(entry, "status"),
            },
            {
              kind: "shift_type_id",
              kindLabel: "班別",
              values: this.buildDayValues(entry, "shift_type_id"),
            },
            {
              kind: "clock",
              kindLabel: "打卡",
              values: this.buildDayValues(entry, "clock"),
            },
          ],
        };
      });
    },
  },
  methods: {
    formatScheduleType,
    getBlockRemarkMessages(block) {
      if (!block) return [];
      const errorIssues = (Array.isArray(this.errors) ? this.errors : [])
        .filter((err) => {
          if (err == null) return false;
          if (err.day !== null && err.day !== undefined) return false;
          const emp = err.employee || {};
          const byUserId =
            emp.user_id != null &&
            String(block.user_id) === String(emp.user_id);
          const byEmpId =
            emp.emp_id != null && String(block.emp_id) === String(emp.emp_id);
          return byUserId || byEmpId;
        })
        .map((err) => ({ text: err.message, isError: true }));

      const warningIssues = (Array.isArray(this.warnings) ? this.warnings : [])
        .filter((err) => {
          if (err == null) return false;
          if (err.day !== null && err.day !== undefined) return false;
          const emp = err.employee || {};
          const byUserId =
            emp.user_id != null &&
            String(block.user_id) === String(emp.user_id);
          const byEmpId =
            emp.emp_id != null && String(block.emp_id) === String(emp.emp_id);
          return byUserId || byEmpId;
        })
        .map((err) => ({ text: err.message, isError: false, isWarning: true }));

      const userSummaries = (
        Array.isArray(this.nationalHolidaySummaries)
          ? this.nationalHolidaySummaries
          : []
      ).filter((summary) => {
        if (summary == null) return false;
        const userId = summary.user_id ?? summary.user?.id ?? null;
        const memberId = summary.user?.member_id ?? null;
        const byUserId =
          userId != null && String(block.user_id) === String(userId);
        const byEmpId =
          memberId != null && String(block.emp_id) === String(memberId);
        return byUserId || byEmpId;
      });

      const summaryIssues =
        userSummaries.length > 0
          ? (() => {
              const cmpYear = Number(this.year);
              const cmpMonth = Number(this.month);
              const asNum = (v) => Number(v ?? 0) || 0;

              const monthSummaries = userSummaries.filter((s) => {
                return (
                  Number(s.year) === cmpYear && Number(s.month) === cmpMonth
                );
              });
              const prevSummaries = userSummaries.filter((s) => {
                const y = Number(s.year);
                const m = Number(s.month);
                if (!Number.isFinite(y) || !Number.isFinite(m)) return false;
                return y < cmpYear || (y === cmpYear && m < cmpMonth);
              });

              const monthTotals = monthSummaries.reduce(
                (acc, s) => {
                  acc.missing += asNum(s.missing_holiday_count);
                  acc.used += asNum(s.used_holiday_count);
                  return acc;
                },
                { missing: 0, used: 0 }
              );

              const prevTotals = prevSummaries.reduce(
                (acc, s) => {
                  acc.missing += asNum(s.missing_holiday_count);
                  acc.used += asNum(s.used_holiday_count);
                  return acc;
                },
                { missing: 0, used: 0 }
              );
              // available = missing - used (remaining carry-over pool)
              const cumulativeAvailable =
                monthTotals.missing -
                monthTotals.used +
                (prevTotals.missing - prevTotals.used);

              const msgs = [];
              if (monthTotals.missing > 0) {
                const consumed =
                  monthTotals.used > 0
                    ? `（已被使用 ${monthTotals.used} 天）`
                    : "";
                msgs.push({
                  text: `本月國定假日尚未排班 ${monthTotals.missing} 天${consumed}`,
                  isError: false,
                  isWarning: true,
                });
              }
              if (cumulativeAvailable > 0) {
                msgs.push({
                  text: `累計可用結餘 ${cumulativeAvailable} 天`,
                  isError: false,
                  isWarning: true,
                });
              }
              return msgs;
            })()
          : [];

      const allIssues = [...errorIssues, ...warningIssues, ...summaryIssues];
      const seen = new Set();
      const unique = [];
      for (const item of allIssues) {
        if (!seen.has(item.text)) {
          seen.add(item.text);
          unique.push(item);
        }
      }
      return unique;
    },
    buildDayValues(entry, field) {
      const values = {};
      const daysObj = (entry && entry.days) || {};
      for (let d = 1; d <= 31; d += 1) {
        let v;
        const day = daysObj[String(d)] || daysObj[d];
        if (day) {
          v = day[field];
        } else {
          v = entry[`day${d}_${field}`];
        }

        if (v === undefined || v === null) {
          values[d] = "";
          continue;
        }

        if (field === "shift_type_id") {
          const st = this.shiftTypes.find((t) => t.id == v);
          values[d] = st ? st.code : String(v || "");
        } else if (field === "clock") {
          if (v == null || v === "") {
            values[d] = "";
          } else if (typeof v === "string") {
            values[d] = v;
          } else {
            values[d] = "✓";
          }
        } else {
          values[d] = String(v);
        }
      }
      return values;
    },
    getCellStyle(kind, value, block, day) {
      let baseStyle = {};

      const sc = block.shiftChanges && block.shiftChanges[day];
      if (sc) {
        // Green background for shift change
        baseStyle = { backgroundColor: "#c6efce", color: "#006100" };
      } else if (kind === "status") {
        let bgColor;
        switch (value) {
          case "特":
            bgColor = "#ccc0d9"; // light purple
            break;
          case "補":
            bgColor = "#fbd4b4"; // light orange
            break;
          case "國":
            bgColor = "#d6e3bc"; // light green
            break;
          case "休":
          case "例":
            bgColor = "#daeef3"; // light blue
            break;
        }
        if (bgColor) {
          baseStyle = { backgroundColor: bgColor, color: "black" };
        }
      }

      // Check for errors
      const hasError = this.errors.some(
        (err) =>
          err.day === day &&
          (String(block.user_id) === String(err.employee?.user_id) ||
            String(block.emp_id) === String(err.employee?.emp_id))
      );

      if (hasError) {
        baseStyle.color = "red";
        baseStyle.fontWeight = "bold";
      }

      return baseStyle;
    },
    getCellTitle(kind, value, block, day) {
      const titles = [];

      // Shift change info
      const sc = block.shiftChanges && block.shiftChanges[day];
      if (sc) {
        titles.push(`與 ${sc.otherName} 交換 (對應日: ${sc.otherDay}號)`);
      }

      if (kind === "shift_type_id" && value) {
        const shift = this.shiftTypes.find((t) => t.code === value);
        if (shift) {
          const inTime = shift.clock_in_time || "無";
          const outTime = shift.clock_out_time || "無";
          titles.push(`班別時間: ${inTime} - ${outTime}`);
        }
      }

      const error = this.errors.find(
        (err) =>
          err.day === day &&
          (String(block.user_id) === String(err.employee?.user_id) ||
            String(block.emp_id) === String(err.employee?.emp_id))
      );
      if (error) {
        titles.push(error.message);
      }

      return titles.join("\n");
    },
  },
};
</script>

<style scoped>
.schedule-entry-wrapper {
  margin-top: 16px;
}

.schedule-entry-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
}

.schedule-entry-table {
  overflow: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
}

.warning-remark {
  color: #f57c00;
}

table {
  border-collapse: collapse;
  width: max-content;
  min-width: 100%;
}

th,
td {
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 6px 8px;
  white-space: nowrap;
  text-align: center;
  background: #fff;
}

th {
  background: rgba(0, 0, 0, 0.03);
  font-weight: 600;
}

.day-col {
  min-width: 30px;
}

.summary-col {
  min-width: 80px;
  white-space: nowrap;
}

.remark-col {
  min-width: 220px;
  text-align: left;
  white-space: normal;
}

.remark-line {
  white-space: normal;
}

.error-remark {
  color: red;
}

.existing-entry-cell {
  background: #fff1e6;
}

.existing-entry-label {
  margin-top: 4px;
  font-size: 12px;
  color: #c65911;
  font-weight: 600;
}
</style>
